/**
 * Deciding whether a bureau actually returned a credit score (GH #405).
 *
 * WHY THIS EXISTS
 * ---------------
 * The Experian output component read `parseInt(scoreInfo.score || 0)`. A
 * thin-file borrower, a no-hit, or a report rehydrated from history without a
 * score all yield `undefined` — which became `0`, and 0 falls through every
 * band boundary to the default, so the screen showed a giant red **0** tagged
 * **"Poor"** with a 0%-of-900 gauge. A DSA reading that sees a genuine-looking
 * credit score of zero for someone whose score was simply not returned. The
 * valid range is 300–900; 0 is not a bad score, it is not a score.
 *
 * Equifax's CreditScoreCard carried the same `|| 0` coercion, shielded only by
 * an `if (!scoreData) return null` that a payload like `{ name: 'Experian' }`
 * walks straight past.
 *
 * "Absent" and "bad" are different answers and must not render the same. That
 * decision is what lives here — the band THRESHOLDS stay with each bureau,
 * because they genuinely differ (CIBIL splits at 700, Experian and Equifax do
 * not) and collapsing them would silently re-grade every report.
 */

/** Bureau scores live in 300–900. Anything outside it is a sentinel, not a score. */
export const SCORE_MIN = 300;
export const SCORE_MAX = 900;

/**
 * The score as a number, or null when the bureau did not return one.
 *
 * Rejects the sentinels providers use for "no hit" — 0, -1, empty string, null,
 * undefined, 'NA' — rather than letting them through as a very low score. That
 * is the whole defect: `|| 0` turned every one of these into a rating.
 *
 * @param {unknown} raw
 * @returns {number|null}
 */
export const parseScore = (raw) => {
    if (raw === null || raw === undefined) return null;
    if (typeof raw === 'string' && raw.trim() === '') return null;

    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    // Out of range means the provider is telling us something other than a
    // rating. Rendering it on a 300–900 gauge would be a category error.
    if (n < SCORE_MIN || n > SCORE_MAX) return null;

    return Math.round(n);
};

/** What the UI shows when there is no score. Never a number, never a grade. */
export const SCORE_UNAVAILABLE = Object.freeze({
    available: false,
    score: null,
    display: 'Not available',
    severity: 'secondary',
    label: 'Not available',
    color: 'text-600',
    pct: 0,
});

/**
 * Bands shared by Experian and Equifax. CIBIL keeps its own — it splits Good at
 * 700, and re-grading its reports is not this issue's business.
 */
export const BUREAU_BANDS = [
    { min: 750, severity: 'success', label: 'Excellent', color: 'text-green-600' },
    { min: 650, severity: 'warning', label: 'Good', color: 'text-orange-600' },
    { min: 550, severity: 'info', label: 'Fair', color: 'text-blue-600' },
    { min: SCORE_MIN, severity: 'danger', label: 'Poor', color: 'text-red-600' },
];

/**
 * Grade a raw score, or say it is unavailable.
 *
 * @param {unknown} raw
 * @param {Array<{min:number, severity:string, label:string, color:string}>} [bands]
 * @returns {{available:boolean, score:number|null, display:string, severity:string, label:string, color:string, pct:number}}
 */
export const scoreBand = (raw, bands = BUREAU_BANDS) => {
    const score = parseScore(raw);
    if (score === null) return SCORE_UNAVAILABLE;

    const band = bands.find((b) => score >= b.min) || bands[bands.length - 1];

    return {
        available: true,
        score,
        display: String(score),
        severity: band.severity,
        label: band.label,
        color: band.color,
        // Measured across the real 300–900 range, not from zero: a 300 is the
        // floor of the scale, and drawing it at 33% of the bar implied a third
        // of a rating that does not exist.
        pct: Math.max(0, Math.min(100, ((score - SCORE_MIN) / (SCORE_MAX - SCORE_MIN)) * 100)),
    };
};

/**
 * A rupee figure, or a dash when the bureau did not report one.
 *
 * Same defect in miniature: `₹{employment.income || '0'}` printed "₹0" for a
 * borrower whose income the report simply did not carry, which reads as
 * "declared zero income" rather than "not reported".
 *
 * @param {unknown} raw
 * @returns {string}
 */
export const formatIncome = (raw) => {
    if (raw === null || raw === undefined) return '—';
    if (typeof raw === 'string' && raw.trim() === '') return '—';

    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return '—';

    return '₹' + n.toLocaleString('en-IN');
};

export default scoreBand;
